import { useEffect, useState } from 'react';
import { recordPageView } from './analytics/beacon';
import Demolition from './pages/Demolition';
import SiegeLab from './pages/SiegeLab';
import DamageLab from './pages/DamageLab';
import ParticleLab from './pages/ParticleLab';
import TextureLab from './pages/TextureLab';
import CottageTown from './pages/CottageTown';
import Smoke from './pages/Smoke';

type Page =
  | 'home'
  | 'demolition'
  | 'siegeLab'
  | 'damageLab'
  | 'particleLab'
  | 'textureLab'
  | 'cottageTown'
  | 'smoke';

// Lightweight path-based routing (no react-router): the SPA reads the URL on
// load and keeps it in sync, so routes can be opened directly.
const PAGE_PATH: Record<Page, string> = {
  home: '/',
  demolition: '/demolition',
  siegeLab: '/siege-lab',
  damageLab: '/damage-lab',
  particleLab: '/particle-lab',
  textureLab: '/texture-lab',
  cottageTown: '/cottage-town',
  smoke: '/smoke',
};

const PAGE_TITLE: Record<Page, string> = {
  home: 'Destruction Lab',
  demolition: 'Demolition Lab — Voronoi fracture',
  siegeLab: 'Siege Lab — combined demolition',
  damageLab: 'Damage Lab — brick ledger',
  particleLab: 'Particle Lab — GPU grains + piles',
  textureLab: 'Texture Lab — customizable surfaces',
  cottageTown: 'Cottage Town',
  smoke: 'Box3D smoke check',
};

function pathToPage(path: string): Page {
  if (path.startsWith('/demolition')) return 'demolition';
  if (path.startsWith('/siege-lab')) return 'siegeLab';
  if (path.startsWith('/damage-lab')) return 'damageLab';
  if (path.startsWith('/particle-lab')) return 'particleLab';
  if (path.startsWith('/texture-lab')) return 'textureLab';
  if (path.startsWith('/cottage-town') || path.startsWith('/cottage') || path.startsWith('/town')) return 'cottageTown';
  if (path.startsWith('/smoke')) return 'smoke';
  return 'home';
}

function App() {
  const [page, setPage] = useState<Page>(() => pathToPage(window.location.pathname));

  // Sync state when the user uses the browser back/forward buttons.
  useEffect(() => {
    const onPop = () => setPage(pathToPage(window.location.pathname));
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  useEffect(() => {
    document.title = PAGE_TITLE[page];
  }, [page]);

  // SwapAnalytics page-view beacon: fires on initial load and on every
  // SPA route change.
  useEffect(() => {
    recordPageView(window.location.pathname);
  }, [page]);

  const goHome = () => {
    setPage('home');
    if (window.location.pathname !== PAGE_PATH.home) {
      window.history.pushState({}, '', PAGE_PATH.home);
    }
  };

  if (page === 'demolition') return <Demolition onBack={goHome} />;
  if (page === 'siegeLab') return <SiegeLab onBack={goHome} />;
  if (page === 'damageLab') return <DamageLab onBack={goHome} />;
  if (page === 'particleLab') return <ParticleLab onBack={goHome} />;
  if (page === 'textureLab') return <TextureLab onBack={goHome} />;
  if (page === 'cottageTown') return <CottageTown onBack={goHome} />;
  if (page === 'smoke') return <Smoke />;

  const labs: Page[] = [
    'demolition',
    'siegeLab',
    'damageLab',
    'particleLab',
    'textureLab',
    'cottageTown',
  ];

  return (
    <main style={{ padding: '2rem', maxWidth: 720 }}>
      <h1>Destruction Lab</h1>
      <p>Layered physics destruction demos on the Box3D engine.</p>
      <ul style={{ lineHeight: 2 }}>
        {labs.map((lab) => (
          <li key={lab}>
            <a href={PAGE_PATH[lab]}>{PAGE_TITLE[lab]}</a>
          </li>
        ))}
      </ul>
    </main>
  );
}

export default App;
